
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import EditProfileDialog from './EditProfileDialog';

interface ProfileSectionProps {
  userId: string;
}

const ProfileSection = ({ userId }: ProfileSectionProps) => {
  const { data: profile, isLoading, error, refetch } = useQuery({
    queryKey: ['profile', userId],
    queryFn: async () => {
      console.log('Fetching profile for user:', userId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching profile:', error);
        throw error;
      }
      
      console.log('Profile data:', data);
      return data;
    },
  });

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User2 className="h-5 w-5" />
            Profile
          </CardTitle>
          <CardDescription>Manage your profile information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-destructive mb-4">
            <AlertCircle className="h-5 w-5" />
            <p>Error loading profile data. Please try again later.</p>
          </div>
          <Button onClick={() => refetch()}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User2 className="h-5 w-5" />
          Profile
        </CardTitle>
        <CardDescription>Manage your profile information</CardDescription>
      </CardHeader>
      <CardContent className="flex items-start gap-6">
        {isLoading ? (
          <div className="w-full flex items-center justify-center">
            <div className="animate-pulse flex space-x-4 w-full">
              <div className="rounded-full bg-muted h-16 w-16"></div>
              <div className="flex-1 space-y-3">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
                <div className="h-4 bg-muted rounded w-1/4"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <Avatar className="h-20 w-20">
              <AvatarImage src={undefined} />
              <AvatarFallback>
                {profile?.full_name?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-4 flex-1">
              <div>
                <h3 className="font-medium">{profile?.full_name || 'Not set'}</h3>
                <p className="text-sm text-muted-foreground">{profile?.email}</p>
                <p className="text-sm text-muted-foreground">Company: {profile?.company || 'Not set'}</p>
              </div>
              <EditProfileDialog userId={userId} initialData={profile} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfileSection;
